/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import StepDate from 'components/stepper/StepDate.tsx';
import StepIcon from 'components/stepper/StepIcon';
import StepLabelStyled from 'components/stepper/StepLabelStyled';
import StepperStyled, { StepperStep } from 'components/stepper/StepperStyled';
import { Tooltip } from 'components/ui/tooltip';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { INVOICE_LEARNING_KEYS } from 'pages/invoices/invoiceLearningHints';
import type { ReactNode } from 'react';
import { InvoiceExternalEventDto, InvoiceMatterDetailsDto, InvoiceStatusType } from 'types/invoices.ts';
import { t } from 'utils/getTranslations';

export interface InvoiceWorkflowStep {
    status: InvoiceStatusType;
    isSkipped: boolean;
    label: string;
    content?: ReactNode;
    externalEvents?: InvoiceExternalEventDto[];
    subSteps?: { label: string; date?: string }[];
}

interface WorkflowProps {
    steps: InvoiceWorkflowStep[];
    activeStep: number;
    invoiceMatters: InvoiceMatterDetailsDto[];
    isMultiApproval: boolean;
    isExternalApproval: boolean;
    footer?: React.ReactNode;
}

/** A nested stepper under a step, pulled in so its lines meet the main one. */
const SUB_STEPPER_CLASS = '-my-[3px] ml-4';
const LABEL_ROW_CLASS = 'flex cursor-default justify-between gap-2';
const SUB_LABEL_CLASS = 'label cursor-default text-xs whitespace-nowrap';
const SUB_DATE_CLASS = 'text-fg-secondary text-[11px] new:text-caption whitespace-nowrap';

type SubStep = { key: string | number; isReached: boolean; label: ReactNode; aside?: ReactNode; tooltip?: string };

/** Sub-steps under one step. The first and last dots draw a short line back to the step's own line. */
function SubStepper({ subSteps, stepCompleted }: { subSteps: SubStep[]; stepCompleted: boolean }) {
    const steps: StepperStep[] = subSteps.map((subStep, index) => {
        const label = (
            <StepLabelStyled
                active={subStep.isReached}
                completed={stepCompleted}
                icon={
                    <StepIcon
                        active={subStep.isReached}
                        completed={stepCompleted}
                        withLeadingLine={index === 0 || index === subSteps.length - 1}
                    />
                }
            >
                <div className={LABEL_ROW_CLASS}>
                    <p className={SUB_LABEL_CLASS}>{subStep.label}</p>
                    {subStep.aside}
                </div>
            </StepLabelStyled>
        );
        return {
            key: subStep.key,
            disabled: !subStep.isReached,
            node: subStep.tooltip ? (
                <Tooltip content={subStep.tooltip} side="top">
                    <div>{label}</div>
                </Tooltip>
            ) : (
                label
            ),
        };
    });
    return <StepperStyled className={SUB_STEPPER_CLASS} steps={steps} />;
}

export default function Workflow({
    steps,
    activeStep,
    footer,
    invoiceMatters,
    isMultiApproval,
    isExternalApproval,
}: WorkflowProps) {
    const effectiveFormat = useEffectiveDateFormat();

    const getMultiApprovalSteps = (
        stepStatus: InvoiceStatusType.REVIEWED | InvoiceStatusType.APPROVED,
        stepCompleted: boolean
    ) => (
        <SubStepper
            stepCompleted={stepCompleted}
            subSteps={invoiceMatters
                .slice()
                .sort((a, b) =>
                    stepStatus === InvoiceStatusType.REVIEWED
                        ? (a.reviewedAt || '9999-01-01').localeCompare(b.reviewedAt || '9999-01-01')
                        : (a.approvedAt || '9999-01-01').localeCompare(b.approvedAt || '9999-01-01')
                )
                .map((item) => {
                    const isSubStepActive =
                        stepStatus === InvoiceStatusType.REVIEWED ? !!item.reviewedAt : !!item.approvedAt;
                    const date = stepStatus === InvoiceStatusType.REVIEWED ? item.reviewedAt : item.approvedAt;
                    const employee =
                        stepStatus === InvoiceStatusType.REVIEWED ? item.reviewedByEmployee : item.approvedByEmployee;
                    return {
                        key: item.matterId,
                        isReached: isSubStepActive,
                        label: item.matterNumber,
                        aside:
                            isSubStepActive && date && employee ? (
                                <StepDate changedByEmployee={employee} date={format(new Date(date), effectiveFormat)} />
                            ) : null,
                    };
                })}
        />
    );

    const getSubSteps = (subSteps: { label: string; date?: string }[], stepCompleted: boolean) => (
        <SubStepper
            stepCompleted={stepCompleted}
            subSteps={subSteps.map((subStep, index) => ({
                key: `sub-step-${index}`,
                isReached: true,
                label: subStep.label,
                aside: subStep.date ? <p className={SUB_DATE_CLASS}>{subStep.date}</p> : null,
            }))}
        />
    );

    const getExternalEventSteps = (events: InvoiceExternalEventDto[], stepCompleted: boolean) => (
        <SubStepper
            stepCompleted={stepCompleted}
            subSteps={events.map((event) => {
                const directionLabel =
                    event.directionTypeCode === 'INBOUND'
                        ? t('invoices.ksef.event.inbound')
                        : t('invoices.ksef.event.outbound');
                const stateText = event.externalInvoiceState ? ` (${event.externalInvoiceState})` : '';
                const dateText = event.eventTimestamp ? format(new Date(event.eventTimestamp), effectiveFormat) : '';
                return {
                    key: event.id,
                    isReached: true,
                    label: `${directionLabel}${stateText}`,
                    aside: dateText ? <p className={SUB_DATE_CLASS}>{dateText}</p> : null,
                    tooltip: event.externalInvoiceStateReason || undefined,
                };
            })}
        />
    );

    const workflowSteps: StepperStep[] = steps.flatMap((step, index) => {
        const isStepActive = index === activeStep || (index < activeStep && !step.isSkipped);
        const isStepCompleted = index < activeStep && !step.isSkipped;
        const isStepCompletedOrActive = isStepCompleted || isStepActive;
        const isContentSkipped =
            isMultiApproval &&
            (step.status === InvoiceStatusType.REVIEWED || step.status === InvoiceStatusType.APPROVED);

        const rows: StepperStep[] = [
            {
                key: step.label,
                disabled: !isStepCompletedOrActive,
                node: (
                    <StepLabelStyled
                        active={isStepActive}
                        completed={isStepCompleted}
                        icon={<StepIcon active={isStepActive} completed={isStepCompleted} />}
                    >
                        <div className={LABEL_ROW_CLASS} data-learning={INVOICE_LEARNING_KEYS.step(step.status)}>
                            <p className="label">{step.label}</p>
                            {isStepCompletedOrActive && !isContentSkipped ? step.content : null}
                        </div>
                    </StepLabelStyled>
                ),
            },
        ];
        const subStepper = (key: string, node: ReactNode) =>
            rows.push({ key: `${step.label}-${key}`, disabled: !isStepCompletedOrActive, node });

        if (isMultiApproval && step.status === InvoiceStatusType.REVIEWED) {
            const stepCompleted = invoiceMatters.every((item) => !!item.reviewedAt);
            subStepper('multi-review-steps', getMultiApprovalSteps(InvoiceStatusType.REVIEWED, stepCompleted));
        } else if (isMultiApproval && step.status === InvoiceStatusType.APPROVED) {
            const stepCompleted = invoiceMatters.every((item) => !!item.approvedAt);
            subStepper('multi-approve-steps', getMultiApprovalSteps(InvoiceStatusType.APPROVED, stepCompleted));
        } else if (
            isExternalApproval &&
            step.status === InvoiceStatusType.FINALIZED &&
            step.externalEvents &&
            step.externalEvents.length > 0
        ) {
            subStepper('external-event-steps', getExternalEventSteps(step.externalEvents, isStepCompleted));
            if (step.subSteps && step.subSteps.length > 0 && isStepCompletedOrActive) {
                subStepper('sub-steps', getSubSteps(step.subSteps, isStepCompleted));
            }
        } else if (step.subSteps && step.subSteps.length > 0 && isStepCompletedOrActive) {
            subStepper('sub-steps', getSubSteps(step.subSteps, isStepCompleted));
        }

        return rows;
    });

    return (
        <div className="flex flex-col gap-2">
            <div data-learning={INVOICE_LEARNING_KEYS.workflow}>
                <StepperStyled steps={workflowSteps} />
            </div>
            {footer ? <div data-learning={INVOICE_LEARNING_KEYS.actions}>{footer}</div> : null}
        </div>
    );
}
